import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Badge,
  Button,
  DateField,
  EmptyState,
  Field,
  Page,
  PageHeader,
  buttonClasses,
  cardClasses,
  cn,
} from '@evogard/ui';
import {
  DESK_BAND_LABELS,
  DESK_DUE_BANDS,
  ZERO,
  deskColumnKey,
  format as formatMoney,
  formatInstant,
  mergeDeskStreams,
  parseMoneyInput,
  sum,
  toColumn,
  type DeskColumn,
  type DeskOperatorGroup,
} from '@evogard/domain';
import type { DeskCan, DeskSummary } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { useSavePreferences } from '../../../lib/session.js';
import {
  DESK_SECTIONS,
  cardPlace,
  columnTotals,
  mergeOperators,
  operatorColumnTitle,
  orderOperators,
  type BoardCard,
  type BoardOperator,
  type DeskTenant,
  type TenantSummary,
} from './deskBoard.js';
import { CardDrawer } from './CardDrawer.js';
import { CreateCardDialog, DistributeDialog } from './DeskDialogs.js';
import { DeskCardView } from './DeskCardView.js';
import { cardDomId, DeskFocusProvider, findColumn, useDeskFocus } from './deskFocus.js';
import { DeskMoveProvider } from './DeskMoveDialog.js';
import { DESK_KEY, useDeskSummaries, type DeskFilters } from './useDesk.js';

const PAGE = 30;

/** A chave da Mesa em `preferences.collapsedColumns`. */
const BOARD = 'billing.desk';

/**
 * A Mesa de cobrança — o quadro que substitui o `KanbanCobranca` (19/10/2026).
 *
 * CE e GO no mesmo quadro: cada coluna soma as bases, e cada card leva o selo
 * da base dele. A tela pede uma base de cada vez (`/billing/desk/:tenant/...`)
 * e junta — a base em que a pessoa não pode ver a Mesa fica fora, avisada.
 *
 * O quadro é dividido em faixas, cada uma com a fila mãe (os cards sem dono)
 * e depois a coluna de cada colaborador: Vencimento, 6 a 30, 31 ou mais,
 * Receptiva e Pagos (pedido do dono, 20/10/2026). No legado eram três blocos
 * (`App.tsx:5424-5620`), com a fila de vencimento em seis colunas e o atraso
 * num bloco só. Cada faixa tem um fundo próprio (`DESK_SECTIONS`), e a coluna
 * da pessoa logada vem logo depois da fila mãe, destacada.
 *
 * A diferença para o `Kanban` da casa, que pinta só o cabeçalho da fase
 * (`Kanban.tsx`, `CARD_SHELL`), é de propósito: aqui a cor é da FAIXA, e o
 * dono a pediu no fundo da coluna, para separar as faixas de longe.
 *
 * As colunas nascem ABERTAS (pedido do dono, 20/10/2026), e a pessoa recolhe as
 * que não quer ver. O que ela recolheu fica nas preferências dela, no servidor
 * (`preferences.collapsedColumns`), e vale na próxima visita e em outro
 * computador. Cada coluna aberta pede ao servidor só a base que tem card nela.
 */
export function DeskBoardScreen() {
  return (
    <DeskFocusProvider>
      <DeskMoveProvider>
        <Board />
      </DeskMoveProvider>
    </DeskFocusProvider>
  );
}

function Board() {
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [paidFrom, setPaidFrom] = useState('');
  const [paidTo, setPaidTo] = useState('');
  const [search, setSearch] = useState('');
  const [openCard, setOpenCard] = useState<{ tenant: DeskTenant; id: number } | null>(null);
  const focus = useDeskFocus();
  const [distribute, setDistribute] = useState<DistributeRequest | null>(null);
  const [creating, setCreating] = useState(false);

  const filters: DeskFilters = {
    min: moneyOrUndefined(min),
    max: moneyOrUndefined(max),
    paidFrom: paidFrom || undefined,
    paidTo: paidTo || undefined,
  };
  const { context, ready, refused, errors, loading } = useDeskSummaries(filters);
  const me = context?.user.id ?? '';

  /*
   * As colunas recolhidas: a lista gravada, até a pessoa mexer; daí em diante a
   * da tela, que vai para o servidor a cada clique. Sem esperar a resposta: a
   * coluna fecha na hora, e a gravação é só para a próxima visita.
   *
   * O `?.` em `collapsedColumns` é para a API de uma versão antes: a interna e a
   * API sobem separadas, e uma API sem o campo derrubava a Mesa inteira com
   * "Cannot read properties of undefined" (homologação, 26/10/2026). Sem o
   * campo, a coluna nasce aberta, que é o padrão.
   */
  const savePreferences = useSavePreferences();
  const [touched, setTouched] = useState<string[] | null>(null);
  const collapsed = touched ?? context?.preferences.collapsedColumns?.[BOARD] ?? [];
  const setCollapsed = (id: string, value: boolean) => {
    const next = value ? [...new Set([...collapsed, id])] : collapsed.filter((c) => c !== id);
    setTouched(next);
    savePreferences.mutate({ collapsedColumns: { [BOARD]: next } });
  };

  const any = (pick: (c: DeskCan) => boolean) => ready.some((s) => pick(s.summary.can));
  const summaryOf = (slug: string) => ready.find((s) => s.tenant.slug === slug)?.summary;

  /*
   * "Mostrar no quadro" para um card cuja coluna esta pessoa não vê — a fila de
   * vencimento de quem não puxa, a coluna que o filtro de valor esvaziou: em
   * vez de rolar para lugar nenhum, abre o card na lateral, que diz onde ele está.
   */
  const target = focus.target;
  useEffect(() => {
    if (!target) return;
    const timer = setTimeout(() => {
      const key = target.card.column;
      if (key !== null && findColumn(key)) return;
      setOpenCard({ tenant: target.card.tenant, id: target.card.id });
      focus.clear();
    }, 800);
    return () => clearTimeout(timer);
  }, [target, focus]);

  const operators = mergeOperators(ready);
  const active = orderOperators(
    operators.filter((o) => o.activeFlow),
    me,
  );
  /*
   * Na receptiva, uma coluna para cada operador da lista com o fluxo receptivo,
   * vazia ou não — como no Kanban, que desenha "Receptiva - <nome>" para todo
   * `cobranca_receptiva` e `cobranca_mista` (decisão do dono, 27/10/2026). Quem
   * saiu da lista só aparece enquanto ainda tem card.
   */
  const receptive = orderOperators(
    operators.filter(
      (o) =>
        o.receptiveFlow &&
        (o.listed ||
          o.userId === me ||
          columnTotals(ready, deskColumnKey({ kind: 'operador', operatorId: o.userId, group: 'receptiva' }))
            .count > 0),
    ),
    me,
  );
  const wide = any((c) => c.distribute);
  const synced = ready
    .map((s) => s.summary.syncedAt)
    .filter((v): v is string => v !== null)
    .sort()[0];
  const agingPending = ready.reduce((n, s) => n + s.summary.agingPending, 0);

  /* o card aberto na lateral, para ele ficar marcado no quadro */
  const isOpen = (card: BoardCard) =>
    openCard !== null && card.id === openCard.id && card.tenant.slug === openCard.tenant.slug;

  const columnProps = {
    summaries: ready,
    filters,
    me,
    summaryOf,
    collapsed,
    onCollapse: setCollapsed,
    onOpen: (card: BoardCard) => setOpenCard({ tenant: card.tenant, id: card.id }),
    isOpen,
  };
  const operatorColumns = (
    group: DeskOperatorGroup,
    tone: string,
    people: BoardOperator[],
  ) =>
    people.map((o) => (
      <OperatorColumn
        key={`${o.userId}-${group}`}
        {...columnProps}
        operator={o}
        group={group}
        tone={tone}
        wide={wide}
        onDistribute={setDistribute}
      />
    ));

  return (
    <Page measure="cheia">
      <PageHeader
        title="Mesa de cobrança"
        actions={
          <div className="flex flex-wrap gap-2">
            {any((c) => c.follow) && (
              <Link to="/billing/desk/moves" className={buttonClasses({ variant: 'contorno' })}>
                Movimentações
              </Link>
            )}
            {any((c) => c.import) && (
              <Link to="/billing/desk/import" className={buttonClasses({ variant: 'contorno' })}>
                Importar inadimplentes
              </Link>
            )}
            {any((c) => c.configure) && (
              <Link to="/billing/desk/settings" className={buttonClasses({ variant: 'contorno' })}>
                Configurar a Mesa
              </Link>
            )}
            {any((c) => c.create || c.receptive) && (
              <Button icon="mais" onClick={() => setCreating(true)}>
                Novo card
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <Field
            label="Buscar"
            placeholder="Nome, telefone ou placa"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-32">
          <Field label="Valor mínimo" inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} />
        </div>
        <div className="w-32">
          <Field label="Valor máximo" inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} />
        </div>
        <div className="w-44">
          <DateField label="Pagos de" value={paidFrom} onChange={setPaidFrom} />
        </div>
        <div className="w-44">
          <DateField label="Pagos até" value={paidTo} onChange={setPaidTo} />
        </div>
      </div>

      {errors.map((e) => (
        <Alert key={e} tone="erro">
          {e}
        </Alert>
      ))}
      {refused.length > 0 && (
        <Alert tone="informacao">
          Você não vê a Mesa de {refused.map((t) => t.name).join(' e ')}: lá seu grupo não tem a
          permissão de ver a Mesa.
        </Alert>
      )}
      {(synced || agingPending > 0) && (
        <p className="text-xs text-texto-suave">
          {synced && <>Boletos dos cards lidos no SGA desde {formatInstant(synced)}. </>}
          {agingPending > 0 && (
            <>
              {agingPending} card(s) mudaram de faixa e já saíram da coluna do operador; o registro
              disso entra na próxima passada.
            </>
          )}
        </p>
      )}

      {search.trim().length >= 2 && (
        <SearchResults
          q={search.trim()}
          summaries={ready}
          summaryOf={summaryOf}
          onOpen={(card) => setOpenCard({ tenant: card.tenant, id: card.id })}
          isOpen={isOpen}
        />
      )}

      {!loading && ready.length === 0 && errors.length === 0 && (
        <EmptyState icon="cobranca" title="Nenhuma Mesa para mostrar">
          Seu grupo não tem a permissão de ver a Mesa em nenhuma das suas bases.
        </EmptyState>
      )}

      {ready.length > 0 && (
        <div className="flex overflow-x-auto pb-4">
          <Section {...DESK_SECTIONS.due} first>
            {any((c) => c.assign) && (
              <Column
                {...columnProps}
                columns={DESK_DUE_BANDS.map((band) => ({ kind: 'fila', band }))}
                tone={DESK_SECTIONS.due.tone}
                title="Fila mãe"
                dialogTitle="Fila mãe — Vencimento"
                subtitle="Puxe para atender"
                onDistribute={wide ? setDistribute : undefined}
              />
            )}
            {operatorColumns('vencimento', DESK_SECTIONS.due.tone, active)}
          </Section>

          <Section {...DESK_SECTIONS.late}>
            <Column
              {...columnProps}
              columns={[{ kind: 'fila', band: 'atraso_6_30' }]}
              tone={DESK_SECTIONS.late.tone}
              title="Fila mãe"
              dialogTitle={`Fila mãe — ${DESK_BAND_LABELS.atraso_6_30}`}
              subtitle="Quem distribui reparte"
              onDistribute={wide ? setDistribute : undefined}
            />
            {operatorColumns('atraso_6_30', DESK_SECTIONS.late.tone, active)}
          </Section>

          <Section {...DESK_SECTIONS.longLate}>
            <Column
              {...columnProps}
              columns={[{ kind: 'fila', band: 'atraso_31_mais' }]}
              tone={DESK_SECTIONS.longLate.tone}
              title="Fila mãe"
              dialogTitle={`Fila mãe — ${DESK_BAND_LABELS.atraso_31_mais}`}
              subtitle="Quem distribui reparte"
              onDistribute={wide ? setDistribute : undefined}
            />
            {operatorColumns('atraso_31_mais', DESK_SECTIONS.longLate.tone, active)}
          </Section>

          {receptive.length > 0 && (
            <Section {...DESK_SECTIONS.receptive}>
              {operatorColumns('receptiva', DESK_SECTIONS.receptive.tone, receptive)}
            </Section>
          )}

          <Section {...DESK_SECTIONS.paid}>
            <Column
              {...columnProps}
              columns={[{ kind: 'pago', outcome: 'pago_automatico' }]}
              tone={DESK_SECTIONS.paid.tone}
              title="Pago automaticamente"
              subtitle="O boleto consta pago na casa"
            />
            <Column
              {...columnProps}
              columns={[{ kind: 'pago', outcome: 'pago_cobranca' }]}
              tone={DESK_SECTIONS.paid.tone}
              title="Pago cobrança"
              subtitle={wide ? 'Com comprovante' : 'Os creditados a você'}
            />
          </Section>
        </div>
      )}

      {openCard && (
        <CardDrawer
          key={`${openCard.tenant.slug}-${openCard.id}`}
          tenant={openCard.tenant}
          id={openCard.id}
          summary={summaryOf(openCard.tenant.slug)}
          onClose={() => setOpenCard(null)}
        />
      )}
      {distribute && (
        <DistributeDialog
          columns={distribute.columns}
          title={distribute.title}
          summaries={ready}
          me={me}
          onClose={() => setDistribute(null)}
        />
      )}
      {creating && <CreateCardDialog summaries={ready} onClose={() => setCreating(false)} />}
    </Page>
  );
}

/* ------------------------------------------------------------------------- */

/** O que "Distribuir" de uma coluna entrega ao diálogo: as chaves dela e o nome inteiro. */
interface DistributeRequest {
  columns: DeskColumn[];
  title: string;
}

/**
 * Uma faixa: o título, a fila mãe e as colunas dos colaboradores. Da segunda em
 * diante leva espaço e uma linha à esquerda — a separação que o dono pediu,
 * junto da cor de fundo de cada uma (`DESK_SECTIONS`).
 */
function Section({
  title,
  subtitle,
  first = false,
  children,
}: {
  title: string;
  subtitle: string;
  tone: string;
  first?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className={cn('flex shrink-0 flex-col gap-3', !first && 'ml-6 border-l-2 border-borda pl-6')}
    >
      <header>
        <h2 className="text-base font-semibold text-texto">{title}</h2>
        <p className="text-xs text-texto-suave">{subtitle}</p>
      </header>
      <div className="flex gap-3">{children}</div>
    </section>
  );
}

interface ColumnShared {
  summaries: TenantSummary[];
  filters: DeskFilters;
  me: string;
  summaryOf: (slug: string) => DeskSummary | undefined;
  onOpen: (card: BoardCard) => void;
  /** O card está aberto na lateral agora. */
  isOpen: (card: BoardCard) => boolean;
  /** As colunas que a pessoa recolheu, pela primeira chave de cada uma. */
  collapsed: readonly string[];
  onCollapse: (id: string, collapsed: boolean) => void;
}

/**
 * A coluna de um colaborador numa faixa. O cabeçalho mostra só o nome — a
 * faixa já está no título dela —, mas o diálogo de redistribuir recebe o nome
 * inteiro ("Atraso 6-30 — Davi Nunes"), porque o Davi tem uma coluna em cada
 * faixa.
 */
function OperatorColumn({
  operator,
  group,
  tone,
  wide,
  onDistribute,
  ...shared
}: ColumnShared & {
  operator: BoardOperator;
  group: DeskOperatorGroup;
  tone: string;
  wide: boolean;
  onDistribute?: (d: DistributeRequest) => void;
}) {
  const column: DeskColumn = { kind: 'operador', operatorId: operator.userId, group };
  const own = operator.userId === shared.me;
  // só quem distribui, mesmo na coluna do próprio operador (decisão do dono, 27/10/2026)
  const canRedistribute = group !== 'receptiva' && wide;
  return (
    <Column
      {...shared}
      columns={[column]}
      tone={tone}
      mine={own}
      title={operator.name}
      dialogTitle={operatorColumnTitle(group, operator.name)}
      subtitle={operator.listed ? undefined : 'Fora da lista de operadores'}
      onDistribute={canRedistribute ? onDistribute : undefined}
      distributeLabel="Redistribuir"
      /*
       * A coluna de quem está na lista aparece mesmo vazia, como no Kanban —
       * quem distribui precisa ver para quem pode mandar (decisão do dono,
       * 27/10/2026). Só a de quem saiu da lista some quando esvazia.
       */
      hideWhenEmpty={!own && !operator.listed}
    />
  );
}

/**
 * Uma coluna: o cabeçalho com contagem e valor somados nas bases, e os cards
 * quando alguém abre. A página é de 30, como o legado (`CARDS_PAGE_SIZE`); as
 * bases intercalam em ordem (`mergeDeskStreams`).
 *
 * Uma coluna pode juntar várias chaves: a fila mãe de vencimento mostra as
 * seis faixas do dia do mês numa só (20/10/2026), porque num dado dia só uma
 * ou duas têm card. Só se pede ao servidor o par (base, chave) que tem card no
 * resumo — cada pedido relê o conjunto aberto da base, e as seis chaves das
 * duas bases seriam doze a cada ato.
 *
 * Quando "Mostrar no quadro" aponta para um card desta coluna (`useDeskFocus`),
 * ela se abre, rola até ele e o destaca. O card que ainda não está entre os
 * carregados aparece no topo, avisado — buscar página atrás de página até ele
 * custaria dezenas de chamadas na 31+.
 *
 * A coluna da própria pessoa (`mine`) leva a borda laranja e o selo "Sua
 * coluna", sobre a cor da faixa. O fundo laranja inteiro foi medido e não
 * entrou: `texto-suave` sobre `primaria-suave` reprova os 4,5:1 no tema claro.
 */
function Column({
  columns,
  title,
  dialogTitle = title,
  subtitle,
  tone,
  mine = false,
  summaries,
  filters,
  summaryOf,
  onOpen,
  isOpen,
  collapsed,
  onCollapse,
  onDistribute,
  distributeLabel = 'Distribuir',
  hideWhenEmpty = false,
}: ColumnShared & {
  columns: DeskColumn[];
  title: string;
  /** O nome inteiro, para o diálogo de distribuir — o cabeçalho pode ser mais curto. */
  dialogTitle?: string;
  subtitle?: string;
  /** O fundo da faixa, de `DESK_SECTIONS`. */
  tone: string;
  mine?: boolean;
  onDistribute?: (d: DistributeRequest) => void;
  distributeLabel?: string;
  hideWhenEmpty?: boolean;
}) {
  const keys = columns.map(deskColumnKey);
  const totals = keys.reduce(
    (acc, key) => {
      const t = columnTotals(summaries, key);
      return { count: acc.count + t.count, amount: sum(acc.amount, t.amount) };
    },
    { count: 0, amount: ZERO },
  );
  const [pages, setPages] = useState(1);

  const focus = useDeskFocus();
  const target =
    focus.target !== null && keys.includes(focus.target.card.column ?? '') ? focus.target : null;
  const [flash, setFlash] = useState(false);
  const scrolled = useRef<number | null>(null);

  /* Aberta, salvo se a pessoa a recolheu — e "Mostrar no quadro" abre por cima disso. */
  const id = keys[0] ?? '';
  const open = !collapsed.includes(id) || target !== null;

  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(false), 2500);
    return () => clearTimeout(timer);
  }, [flash]);

  /* O card trazido, lido de novo: se ele mudou de coluna, deixa de aparecer aqui. */
  const brought = useQuery({
    queryKey: [...DESK_KEY, 'card-trazido', target?.card.tenant.slug, target?.card.id],
    queryFn: async () =>
      ok(
        await api.billingDesk.detail({
          params: { tenant: target!.card.tenant.slug, id: target!.card.id },
        }),
        200,
        'Não foi possível carregar o card.',
      ).card,
    enabled: target !== null,
    placeholderData: target?.card,
  });

  /* Os pares (base, chave) que têm card — e o do card trazido, mesmo que o resumo ainda não o conte. */
  const pairs = open
    ? summaries.flatMap(({ tenant, summary }) =>
        keys
          .filter(
            (key) =>
              (summary.columns.find((c) => c.key === key)?.count ?? 0) > 0 ||
              (target !== null && target.card.tenant.slug === tenant.slug && target.card.column === key),
          )
          .map((key) => ({ tenant, key })),
      )
    : [];

  const results = useQueries({
    queries: pairs.map(({ tenant, key }) => ({
      queryKey: [...DESK_KEY, 'cards', tenant.slug, key, filters, pages],
      queryFn: async () => {
        const items: BoardCard[] = [];
        let cursor: string | undefined;
        let next: string | null = null;
        for (let i = 0; i < pages; i++) {
          const body = ok(
            await api.billingDesk.cards({
              params: { tenant: tenant.slug },
              query: { ...filters, column: key, cursor, limit: PAGE },
            }),
            200,
            'Não foi possível carregar a coluna.',
          );
          items.push(...body.items.map((c) => ({ ...c, tenant })));
          next = body.next;
          if (next === null) break;
          cursor = next;
        }
        return { items, next };
      },
    })),
  });

  /*
   * Rola até o card trazido — uma vez por pedido, e só com a coluna carregada:
   * antes disso ele aparece no topo, e muda de lugar quando a lista chega.
   */
  const settled = results.every((r) => !r.isFetching) && !brought.isFetching;
  useEffect(() => {
    if (!target || !open || !settled || scrolled.current === target.nonce) return;
    const el = document.getElementById(cardDomId(target.card.tenant.slug, target.card.id));
    if (!el) return;
    scrolled.current = target.nonce;
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    setFlash(true);
  });

  if (hideWhenEmpty && totals.count === 0 && !target) return null;

  const merged = mergeDeskStreams(
    results.map((r) => r.data ?? { items: [], next: null }),
    columns[0]?.kind === 'pago' ? 'fechamento' : 'cadastro',
  );
  const failed = results.find((r) => r.error);
  const isTarget = (card: BoardCard) =>
    target !== null && card.id === target.card.id && card.tenant.slug === target.card.tenant.slug;
  const pinned =
    target && brought.data && keys.includes(brought.data.column ?? '') && !merged.visible.some(isTarget)
      ? { ...brought.data, tenant: target.card.tenant }
      : null;
  const empty = totals.count === 0 && pinned === null;

  const view = (card: BoardCard, broughtHere = false) => {
    const summary = summaryOf(card.tenant.slug);
    if (!summary) return null;
    return (
      <DeskCardView
        key={`${card.tenant.slug}-${card.id}`}
        card={card}
        summary={summary}
        highlight={flash && isTarget(card)}
        selected={isOpen(card)}
        broughtHere={broughtHere}
        anchored
        onOpen={() => onOpen(card)}
      />
    );
  };

  return (
    <div
      data-desk-keys={keys.join(' ')}
      className={cardClasses({
        className: cn(
          'flex w-96 shrink-0 scroll-m-6 flex-col gap-2',
          tone,
          mine && 'border-2 border-primaria-texto',
        ),
      })}
    >
      <header className="flex flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-texto">{title}</h3>
            {mine && <Badge tone="informacao">Sua coluna</Badge>}
          </div>
          <Badge>{totals.count}</Badge>
        </div>
        {subtitle && <p className="text-xs text-texto-suave">{subtitle}</p>}
        <p className="text-sm text-texto-suave">
          Valor total: <span className="font-medium text-texto">{formatMoney(totals.amount)}</span>
        </p>
        <div className="flex flex-wrap gap-2">
          {(totals.count > 0 || pinned) && (
            <Button
              size="sm"
              variant="contorno"
              onClick={() => {
                if (open && target) focus.clear();
                onCollapse(id, open);
              }}
            >
              {open ? 'Recolher coluna' : 'Ver cards'}
            </Button>
          )}
          {onDistribute && totals.count > 0 && (
            <Button
              size="sm"
              variant="discreto"
              icon="pessoas"
              onClick={() => onDistribute({ columns, title: dialogTitle })}
            >
              {distributeLabel}
            </Button>
          )}
        </div>
      </header>

      {empty && <p className="text-xs text-texto-suave">Nenhum card nesta coluna.</p>}
      {failed?.error && (
        <p role="alert" className="text-xs font-medium text-erro">
          {failed.error.message}
        </p>
      )}

      {open && (
        <div className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto p-1">
          {pinned && view(pinned, true)}
          {merged.visible.map((card) => view(card))}
          {merged.hasMore && (
            <Button
              size="sm"
              variant="discreto"
              loading={results.some((r) => r.isFetching)}
              onClick={() => setPages((p) => p + 1)}
            >
              Carregar mais
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A busca do cabeçalho — `GET /api/clientes/search` do legado, nas duas bases.
 *
 * Cada card achado diz onde está — "Fila — Vencimento 15", "Atraso 6-30 —
 * Claudenir", "Pago cobrança" — e "Mostrar no quadro" leva até a coluna dele
 * (relato de 20/10/2026: o achado não dizia onde estava, nem levava até lá).
 */
function SearchResults({
  q,
  summaries,
  summaryOf,
  onOpen,
  isOpen,
}: {
  q: string;
  summaries: TenantSummary[];
  summaryOf: (slug: string) => DeskSummary | undefined;
  onOpen: (card: BoardCard) => void;
  isOpen: (card: BoardCard) => boolean;
}) {
  const focus = useDeskFocus();
  const results = useQueries({
    queries: summaries.map(({ tenant }) => ({
      queryKey: [...DESK_KEY, 'busca', tenant.slug, q],
      queryFn: async () =>
        ok(
          await api.billingDesk.search({ params: { tenant: tenant.slug }, query: { q } }),
          200,
          'Não foi possível buscar.',
        ).items.map((c) => ({ ...c, tenant })),
    })),
  });
  const items = results.flatMap((r) => r.data ?? []);
  const loading = results.some((r) => r.isLoading);

  return (
    <section aria-label="Resultado da busca" className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold text-texto-suave">
        {loading ? 'Buscando…' : `${items.length} card(s) encontrados`}
      </h2>
      <div className="flex flex-wrap gap-3">
        {items.map((card) => {
          const summary = summaryOf(card.tenant.slug);
          if (!summary) return null;
          return (
            <div key={`${card.tenant.slug}-${card.id}`} className="flex w-96 flex-col gap-2">
              <div className="flex items-center justify-between gap-2 text-sm">
                <p className="min-w-0 text-texto-suave">
                  Está em: <strong className="text-texto">{cardPlace(card)}</strong>
                </p>
                <Button size="sm" variant="contorno" icon="quadro" onClick={() => focus.show(card)}>
                  Mostrar no quadro
                </Button>
              </div>
              <DeskCardView
                card={card}
                summary={summary}
                selected={isOpen(card)}
                onOpen={() => onOpen(card)}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** O que a pessoa digitou no filtro de valor; vazio ou ilegível é "sem filtro". */
function moneyOrUndefined(text: string): string | undefined {
  const value = parseMoneyInput(text);
  return value === null ? undefined : toColumn(value);
}
